import React, { useEffect, useState } from "react";
import { Icon, IconButton } from "@mui/material";
import PropTypes from "prop-types";

import SoftBox from "../../../components/SoftBox";
import SoftTypography from "../../../components/SoftTypography";

import Dropzone from "react-dropzone";
import SoftDropzoneRoot from "../../../components/SoftDropzone/SoftDropzoneRoot";
import { shortText } from "../../../utils/string";
import { useFormContext } from "react-hook-form";

const normalizeLogoUrl = (url) => {
  if (!url || typeof url !== "string") {
    return null;
  }

  const trimmedUrl = url.trim();
  if (!trimmedUrl) {
    return null;
  }

  if (trimmedUrl.startsWith("blob:") || trimmedUrl.startsWith("/")) {
    return trimmedUrl;
  }

  if (trimmedUrl.startsWith("//")) {
    return `${window.location.protocol}${trimmedUrl}`;
  }

  try {
    const parsedUrl = new URL(trimmedUrl);
    if (parsedUrl.hostname === "host.docker.internal" && typeof window !== "undefined") {
      parsedUrl.hostname = window.location.hostname || "localhost";
    }
    return parsedUrl.toString();
  } catch {
    return trimmedUrl;
  }
};

function LogoUpload({ currentLogoUrl }) {
  const [uploadedFile, setUploadedFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [isMarkedForRemoval, setIsMarkedForRemoval] = useState(false);
  const [previewLoadError, setPreviewLoadError] = useState(false);
  const [previewSourceIndex, setPreviewSourceIndex] = useState(0);
  const { setValue } = useFormContext();

  const onFileUpload = (files) => {
    if (files && files.length > 0) {
      if (preview && preview.startsWith("blob:")) {
        URL.revokeObjectURL(preview);
      }
      setUploadedFile(files[0]);
      setValue("logo", files[0]);
      setValue("removeLogo", false);
      setIsMarkedForRemoval(false);
      setPreview(URL.createObjectURL(files[0]));
    }
  };

  const removeLogo = () => {
    const hasPersistedLogo = Boolean(currentLogoUrl);
    if (preview && preview.startsWith("blob:")) {
      URL.revokeObjectURL(preview);
    }
    setUploadedFile(null);
    setPreview(null);
    setPreviewLoadError(false);
    setPreviewSourceIndex(0);
    setIsMarkedForRemoval(hasPersistedLogo);
    setValue("logo", null);
    setValue("removeLogo", hasPersistedLogo);
  };

  useEffect(() => {
    return () => {
      if (preview && preview.startsWith("blob:")) {
        URL.revokeObjectURL(preview);
      }
    };
  }, [preview]);

  const previewCandidates = React.useMemo(() => {
    const source = preview || (isMarkedForRemoval ? null : currentLogoUrl) || null;
    const normalizedSource = normalizeLogoUrl(source);
    return [...new Set([normalizedSource, source].filter(Boolean))];
  }, [preview, currentLogoUrl, isMarkedForRemoval]);

  useEffect(() => {
    setPreviewLoadError(false);
    setPreviewSourceIndex(0);
  }, [preview, currentLogoUrl]);

  useEffect(() => {
    setUploadedFile(null);
    setIsMarkedForRemoval(false);
    setPreview((previousPreview) => {
      if (previousPreview && previousPreview.startsWith("blob:")) {
        URL.revokeObjectURL(previousPreview);
      }
      return null;
    });
    setValue("logo", null);
    setValue("removeLogo", false);
  }, [currentLogoUrl, setValue]);

  const previewSource = previewCandidates[previewSourceIndex] || null;

  return (
    <SoftBox
      sx={{
        p: 2.5,
        borderRadius: "12px",
        border: "1px solid #e0e0e0",
        backgroundColor: "#fff",
        display: "grid",
        gridTemplateColumns: { xs: "1fr", lg: "280px 1fr" },
        gap: 2,
        alignItems: "stretch",
      }}
    >
      <SoftBox
        sx={{
          minHeight: "180px",
          border: "1px dashed #d2d6da",
          borderRadius: "10px",
          display: "flex",
          flexDirection: "column",
          justifyContent: "flex-start",
          alignItems: "center",
          p: 1.5,
          backgroundColor: "#f8f9fc",
          overflow: "hidden",
        }}
      >
        <SoftTypography variant="button" fontWeight="bold" sx={{ mb: 1 }}>
          Preview
        </SoftTypography>
        {previewSource && !previewLoadError ? (
          <SoftBox
            sx={{
              position: "relative",
              width: "100%",
              flex: 1,
              minHeight: 0,
              alignSelf: "stretch",
            }}
          >
            <SoftBox
              component="img"
              src={previewSource}
              alt="Logo preview"
              onError={() => {
                if (previewSourceIndex < previewCandidates.length - 1) {
                  setPreviewSourceIndex((index) => index + 1);
                  return;
                }
                setPreviewLoadError(true);
              }}
              sx={{ width: "100%", height: "100%", objectFit: "contain" }}
            />
            <IconButton
              size="small"
              onClick={removeLogo}
              sx={{
                position: "absolute",
                top: 6,
                right: 6,
                width: 24,
                height: 24,
                backgroundColor: "rgba(17, 24, 39, 0.72)",
                color: "#fff",
                "&:hover": {
                  backgroundColor: "rgba(17, 24, 39, 0.9)",
                },
              }}
            >
              <Icon fontSize="small">close</Icon>
            </IconButton>
          </SoftBox>
        ) : (
          <SoftBox
            sx={{
              flex: 1,
              width: "100%",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              color: "#8392ab",
              borderRadius: "8px",
              border: "1px dashed #d2d6da",
              backgroundColor: "#fff",
            }}
          >
            <Icon fontSize="medium">image</Icon>
            <SoftTypography variant="caption" color="text" sx={{ mt: 0.5, fontWeight: 600 }}>
              Logo preview
            </SoftTypography>
            <SoftTypography variant="caption2" color="text" sx={{ opacity: 0.7 }}>
              Upload a logo to see it here
            </SoftTypography>
          </SoftBox>
        )}
      </SoftBox>
      <SoftBox sx={{ minHeight: "180px" }}>
        <Dropzone
          onDrop={(acceptedFiles) => onFileUpload(acceptedFiles)}
          maxFiles={1}
          accept={{ "image/jpeg": [], "image/png": [], "image/jpg": [] }}
        >
          {({ getRootProps, getInputProps, isDragActive }) => (
            <SoftDropzoneRoot
              {...getRootProps()}
              sx={{
                height: "100%",
                minHeight: "180px",
                cursor: "pointer",
                border: isDragActive
                  ? "2px dashed #17c1e8 !important"
                  : "1px dashed #d2d6da !important",
                borderRadius: "10px",
                backgroundColor: isDragActive ? "rgba(23, 193, 232, 0.08)" : "#fff",
                transition: "all 0.2s ease",
              }}
              display="flex"
              flexDirection={"column"}
              justifyContent={"center"}
              alignItems={"center"}
            >
              <Icon sx={{ fontSize: "2rem", color: isDragActive ? "#17c1e8" : "#8392ab", mb: 0.5 }}>
                upload
              </Icon>
              <SoftTypography variant="button" fontWeight="regular" textAlign="center">
                Drag logo file here
              </SoftTypography>
              <SoftTypography variant="caption" color="text" sx={{ mt: 0.5 }}>
                or click to browse
              </SoftTypography>
              {uploadedFile && (
                <SoftTypography
                  variant="caption"
                  sx={{
                    mt: 1.25,
                    px: 1.5,
                    py: 0.25,
                    borderRadius: "999px",
                    border: "1px solid #d2d6da",
                    color: "text.main",
                  }}
                >
                  {shortText(uploadedFile.name, 38)}
                </SoftTypography>
              )}
              {!uploadedFile && (
                <SoftTypography variant="caption" color="text" sx={{ mt: 1 }}>
                  Supported: PNG, JPG, JPEG
                </SoftTypography>
              )}

              <SoftBox component="input" name="file" type="file" multiple {...getInputProps()} />
            </SoftDropzoneRoot>
          )}
        </Dropzone>
      </SoftBox>
    </SoftBox>
  );
}

LogoUpload.propTypes = {
  currentLogoUrl: PropTypes.string,
};

export default LogoUpload;
